import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek);

/**
 * Comparison windows for dashboard KPIs and charts (ISO week, calendar months, YTD).
 * Keeps relative date math out of Dashboard.vue.
 *
 * Month comparisons intentionally use completed calendar months only (no MTD),
 * same idea as the fixed 12-month report which ends before the current month.
 */
export function getDashboardComparisonPeriods(referenceDate = dayjs()) {
    const now = dayjs(referenceDate);
    const startThisWeek = now.startOf("isoWeek");
    const endThisWeek = now.endOf("isoWeek");
    const startPrevWeek = startThisWeek.subtract(1, "week");
    const endPrevWeek = endThisWeek.subtract(1, "week");
    const startPrev2Week = startThisWeek.subtract(2, "week");
    const endPrev2Week = endThisWeek.subtract(2, "week");
    const startWeekLastYear = startThisWeek.subtract(1, "year");
    const endWeekLastYear = endThisWeek.subtract(1, "year");

    const startPrevMonth = now.startOf("month").subtract(1, "month");
    const endPrevMonth = now.startOf("month").subtract(1, "day").endOf("day");
    const startMonthBefore = startPrevMonth.subtract(1, "month").startOf("month");
    const endMonthBefore = startPrevMonth.subtract(1, "day").endOf("day");
    const startSameMonthLastYear = startPrevMonth.subtract(1, "year").startOf("month");
    const endSameMonthLastYear = startPrevMonth.subtract(1, "year").endOf("month");

    const startYtd = now.startOf("year");
    const endYtd = now.endOf("day");
    const startYtdPrev = startYtd.subtract(1, "year");
    const endYtdPrev = endYtd.subtract(1, "year");

    return {
        now,
        thisWeekIso: startThisWeek.isoWeek(),
        startThisWeek,
        endThisWeek,
        startPrevWeek,
        endPrevWeek,
        startPrev2Week,
        endPrev2Week,
        startWeekLastYear,
        endWeekLastYear,
        startPrevMonth,
        endPrevMonth,
        startMonthBefore,
        endMonthBefore,
        startSameMonthLastYear,
        endSameMonthLastYear,
        startYtd,
        endYtd,
        startYtdPrev,
        endYtdPrev,
        previousMonthLabel: startPrevMonth.format("MMMM YYYY"),
        monthBeforeLabel: startMonthBefore.format("MMMM YYYY"),
        sameMonthLastYearLabel: startSameMonthLastYear.format("MMMM YYYY"),
    };
}

/**
 * Preceding same-length window for the selected filter range, and a human label.
 * A full calendar month compares against the whole previous calendar month
 * ("previous month"); otherwise the immediately preceding N-day window.
 */
export function getSelectedRangeComparison(startDate, endDate) {
    const start = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).startOf("day");
    const nDays = end.diff(start, "day") + 1;

    const isFullCalendarMonth =
        start.date() === 1 && end.isSame(start.endOf("month"), "day");
    if (isFullCalendarMonth) {
        const prevStart = start.subtract(1, "month").startOf("month");
        return { prevStart, prevEnd: prevStart.endOf("month"), label: "previous month" };
    }

    const prevEnd = start.subtract(1, "day");
    const prevStart = prevEnd.subtract(nDays - 1, "day");
    const label =
        nDays === 7 ? "previous week" : nDays === 1 ? "previous day" : `previous ${nDays} days`;
    return { prevStart, prevEnd, label };
}

/**
 * Bucket size for the selected-timeline chart:
 * ≤ 31 days → day, ≤ 92 days → week, else month.
 */
export function resolveSelectedRangeSubdivision(startDate, endDate) {
    const days = dayjs(endDate).startOf("day").diff(dayjs(startDate).startOf("day"), "day") + 1;
    if (days <= 31) {
        return "day";
    }
    if (days <= 92) {
        return "week";
    }
    return "month";
}

export function selectedRangeChartTitle(subdivision) {
    if (subdivision === "week") {
        return "Bookings by week (selected timeline)";
    }
    if (subdivision === "month") {
        return "Bookings by month (selected timeline)";
    }
    return "Bookings by day (selected timeline)";
}
